import { useState, useRef, useEffect } from "react";
import {
  MapContainer,
  TileLayer,
  LayersControl,
  GeoJSON
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import {
  getAllDistricts,
  gisCoordinatesData,
  gisBlockCoordinatesData,
  gisStateCoordinates
} from "../../../../Services/EndPoints";
import { toast } from "react-toastify";
import { getData } from "../../../../Services/Services";
import Loader from "react-spinner-loader";

/* RMMap Component - Interactive map for displaying districts, blocks, and geographic data */
const RMMap = ({
  selectedDistrictIds,
  selectedBlockIds,
  isLoading,
  selectedRegions
}) => {
  // Full screen functionality
  const handle = useFullScreenHandle();

  // State management
  const [DistrictList, setDistrictList] = useState([]);
  const [loader, setLoader] = useState(false);
  const [districtLayers, setDistrictLayers] = useState({});
  const [blockLayers, setBlockLayers] = useState({});
  const [stateGeoJSON, setStateGeoJSON] = useState(null);
  const [allDistrictsSelected, setAllDistrictsSelected] = useState(false);

  // Refs for map and drawing functionality
  const mapRef = useRef(null);

  /* Check if all districts are selected (used for styling and zoom behavior) */
  useEffect(() => {
    setAllDistrictsSelected(selectedDistrictIds.length === DistrictList.length);
  }, [selectedDistrictIds, DistrictList]);

  /* Auto-zoom to the most recently selected district */
  useEffect(() => {
    if (selectedDistrictIds.length > 0 && mapRef.current) {
      const lastSelectedDistrictId = selectedDistrictIds[selectedDistrictIds.length - 1];
      const districtLayer = districtLayers[lastSelectedDistrictId];

      if (districtLayer) {
        const map = mapRef.current;
        const tempLayer = L.geoJSON(districtLayer);
        const bounds = tempLayer.getBounds();

        // Animate zoom to selected district
        map.flyToBounds(bounds, {
          padding: [50, 50],
          duration: 1
        });
      }
    }
  }, [selectedDistrictIds, districtLayers]);

  /* Auto-zoom to the most recently selected block */
  useEffect(() => {
    if (selectedBlockIds.length > 0 && mapRef.current) {
      const lastSelectedBlockId = selectedBlockIds[selectedBlockIds.length - 1];
      const blockLayer = blockLayers[lastSelectedBlockId];

      if (blockLayer) {
        const map = mapRef.current;
        const tempLayer = L.geoJSON(blockLayer);
        const bounds = tempLayer.getBounds();

        // Animate zoom to selected block with appropriate zoom level
        map.flyToBounds(bounds, {
          padding: [50, 50],
          duration: 1,
          maxZoom: 14
        });
      }
    }
  }, [selectedBlockIds, blockLayers]);

  /* Smart zoom behavior based on selection type and count */
  useEffect(() => {
    if (selectedDistrictIds.length > 0 && mapRef.current) {
      const map = mapRef.current;

      // All districts selected - show entire state
      if (selectedDistrictIds.length === DistrictList.length) {
        if (stateGeoJSON) {
          const tempLayer = L.geoJSON(stateGeoJSON);
          const bounds = tempLayer.getBounds();
          map.flyToBounds(bounds, {
            padding: [50, 50],
            duration: 1,
            maxZoom: 8
          });
        }
      }
      // Single district selected - zoom to district
      else if (selectedDistrictIds.length === 1) {
        const districtId = selectedDistrictIds[0];
        const districtLayer = districtLayers[districtId];

        if (districtLayer) {
          const tempLayer = L.geoJSON(districtLayer);
          const bounds = tempLayer.getBounds();
          map.flyToBounds(bounds, {
            padding: [50, 50],
            duration: 1,
            maxZoom: 10
          });
        }
      }
      // Multiple districts selected - zoom to encompass all
      else {
        const bounds = new L.LatLngBounds();

        selectedDistrictIds.forEach(districtId => {
          const districtLayer = districtLayers[districtId];
          if (districtLayer) {
            const tempLayer = L.geoJSON(districtLayer);
            bounds.extend(tempLayer.getBounds());
          }
        });

        // Fallback to state bounds if no valid district bounds
        if (!bounds.isValid() && stateGeoJSON) {
          const tempLayer = L.geoJSON(stateGeoJSON);
          bounds.extend(tempLayer.getBounds());
        }

        if (bounds.isValid()) {
          map.flyToBounds(bounds, {
            padding: [50, 50],
            duration: 1,
            maxZoom: 9
          });
        }
      }
    }
  }, [selectedDistrictIds, districtLayers, stateGeoJSON, DistrictList.length]);

  /* Fetch and process district and block boundary layers */
  useEffect(() => {
    const fetchLayers = async () => {
      if (isLoading) {
        setLoader(true);
        return;
      }

      try {
        setLoader(true);

        // Fetch district boundary data
        const districtPromises = selectedDistrictIds.map(async (gisId) => {
          try {
            const response = await getData(gisCoordinatesData + gisId);
            return response && response.length > 0
              ? { gisId, geoJson: JSON.parse(response[0].coordinates) }
              : null;
          } catch (error) {
            console.error(`Error fetching district ${gisId}:`, error);
            return null;
          }
        });

        // Fetch block boundary data
        const blockPromises = selectedBlockIds.map(async (gisId) => {
          try {
            const response = await getData(gisBlockCoordinatesData + gisId);
            return response && response.length > 0
              ? { gisId, geoJson: JSON.parse(response[0].coordinates) }
              : null;
          } catch (error) {
            console.error(`Error fetching block ${gisId}:`, error);
            return null;
          }
        });

        // Wait for all requests to complete
        const [districtResults, blockResults] = await Promise.all([
          Promise.all(districtPromises),
          Promise.all(blockPromises)
        ]);

        // Process district results into state
        const newDistrictLayers = {};
        districtResults.forEach(result => {
          if (result) newDistrictLayers[result.gisId] = result.geoJson;
        });

        // Process block results into state
        const newBlockLayers = {};
        blockResults.forEach(result => {
          if (result) newBlockLayers[result.gisId] = result.geoJson;
        });

        setDistrictLayers(newDistrictLayers);
        setBlockLayers(newBlockLayers);
      } catch (error) {
        console.error("Error fetching layers:", error);
      } finally {
        setLoader(false);
      }
    };

    // Only fetch if there are selections, otherwise clear layers
    if (selectedDistrictIds.length > 0 || selectedBlockIds.length > 0) {
      fetchLayers();
    } else {
      setDistrictLayers({});
      setBlockLayers({});
      setLoader(false);
    }
  }, [selectedDistrictIds, selectedBlockIds, isLoading]);

  /* Initialize component data on mount */
  useEffect(() => {
    fetchDistrictList();
    mapStateCoordinates();
  }, []);

  /* Fetch state boundary coordinates (Bihar state) */
  const mapStateCoordinates = async () => {
    try {
      const res = await getData(gisStateCoordinates + 52); // Bihar's state_id is 52

      if (res && res.length > 0) {
        const stateData = res[0];
        // Create GeoJSON feature from coordinates
        const geoJsonData = {
          type: "Feature",
          properties: {
            name: stateData.state_name,
            state_id: stateData.state_id,
          },
          geometry: JSON.parse(stateData.coordinates),
        };
        setStateGeoJSON(geoJsonData);
      }
    } catch (error) {
      console.error("Error fetching state coordinates:", error);
      toast.error("Failed to load state coordinates");
    }
  };

  /* Fetch list of all available districts */
  const fetchDistrictList = async () => {
    try {
      const response = await getData(getAllDistricts);
      if (response && Array.isArray(response)) {
        setDistrictList(response);
      } else {
        console.error("Invalid districts data format:", response);
        toast.error("Failed to load districts data");
      }
    } catch (error) {
      console.error("Error fetching districts:", error);
      toast.error("Failed to load districts data");
    }
  };

  /* Calculate map center based on available branch data */
  const getMapCenter = () => {
    return [25.5941, 85.1376];
  };

  /* Handle district layer click events */
  const handleDistrictClick = (e) => {
    e.originalEvent.preventDefault();
    e.originalEvent.stopPropagation();
    // Zoom to clicked district
    const bounds = e.target.getBounds();
    mapRef.current.flyToBounds(bounds, {
      padding: [50, 50],
      duration: 1
    });
  };

  /* Handle district layer mouseover events */
  const handleDistrictMouseOver = (e) => {
    const layer = e.target;
    layer.setStyle({
      weight: 2,
      color: "#ff0000"
    });
  };

  /*  Handle district layer mouseout events */
  const handleDistrictMouseOut = (e, gisId) => {
    const layer = e.target;
    const isSelected = selectedDistrictIds.includes(gisId);
    layer.setStyle({
      weight: isSelected ? 2 : 1,
      color: isSelected ? "#ff0000" : "#ff7800"
    });
  };

  /* Handle block layer click events */
  const handleBlockClick = (e) => {
    e.originalEvent.preventDefault();
    e.originalEvent.stopPropagation();
    // Zoom to clicked block
    const bounds = e.target.getBounds();
    mapRef.current.flyToBounds(bounds, {
      padding: [50, 50],
      duration: 1
    });
  };

  /* Handle block layer mouseover events */
  const handleBlockMouseOver = (e) => {
    const layer = e.target;
    layer.setStyle({
      weight: 2,
      color: "#ff0000"
    });
  };

  /* Handle block layer mouseout events */
  const handleBlockMouseOut = (e, gisId) => {
    const layer = e.target;
    const isSelected = selectedBlockIds.includes(gisId);
    layer.setStyle({
      weight: isSelected ? 2 : 1,
      color: isSelected ? "#ff0000" : "#0000ff"
    });
  };

  /* Get display name for a region (district or block) */
  const getRegionDisplayName = (gisId, feature, type) => {
    const regionData = selectedRegions.find(region => region.gis_id === parseInt(gisId));
    return regionData?.name ||
      (feature.properties && feature.properties.name) ||
      `${type} ${gisId}`;
  };

  return (
    <FullScreen handle={handle}>
      <div style={{
        display: "flex",
        height: "100vh",
        fontFamily: "Arial, sans-serif",
        position: "relative"
      }}>
        {/* Loading overlay */}
        {loader && (
          <Loader
            show={loader}
            type="body"
            stack="vertical"
            message="Loading Data"
          />
        )}

        {/* Main map container */}
        <div style={{ flex: 3 }}>
          <MapContainer
            ref={mapRef}
            center={getMapCenter()}
            attributionControl={false}
            zoom={7}
            style={{
              width: "100%",
              height: "75%",
            }}
            whenCreated={(mapInstance) => {
              mapRef.current = mapInstance;
            }}
          >
            {/* Map layer controls for different view types */}
            <LayersControl position="topright" collapsed={false}>
              <LayersControl.BaseLayer name="Road View">
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
              </LayersControl.BaseLayer>

              <LayersControl.BaseLayer name="Satellite View">
                <TileLayer
                  attribution=""
                  url="https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
                  maxZoom={20}
                  subdomains={["mt0", "mt1", "mt2", "mt3"]}
                />
              </LayersControl.BaseLayer>

              <LayersControl.BaseLayer checked name="Terrain View">
                <TileLayer
                  attribution=""
                  url="https://{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}"
                  maxZoom={20}
                  subdomains={["mt0", "mt1", "mt2", "mt3"]}
                />
              </LayersControl.BaseLayer>

              <LayersControl.BaseLayer name="Hybrid View">
                <TileLayer
                  attribution=""
                  url="https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
                  maxZoom={20}
                  subdomains={["mt0", "mt1", "mt2", "mt3"]}
                />
              </LayersControl.BaseLayer>
            </LayersControl>

            {/* State boundary layer */}
            {stateGeoJSON && (
              <GeoJSON
                key="state-boundary"
                data={stateGeoJSON}
                style={{
                  color: "#000000",
                  weight: 2,
                  fillColor: "transparent",
                  fillOpacity: 0.2,
                }}
                onEachFeature={(feature, layer) => {
                  if (feature.properties && feature.properties.name) {
                    layer.bindPopup(`State: ${feature.properties.name}`);
                  }
                }}
              />
            )}

            {/* District boundary layers */}
            {Object.entries(districtLayers).map(([gisId, geoJson]) => {
              const regionData = selectedRegions.find(region => region.gis_id === parseInt(gisId));
              const isRecentlySelected = selectedDistrictIds[selectedDistrictIds.length - 1] === gisId;

              return (
                <GeoJSON
                  key={gisId}
                  data={geoJson}
                  style={{
                    color: allDistrictsSelected ? "#ff7800" :
                      isRecentlySelected ? "#ff0000" : "#ff7800",
                    weight: allDistrictsSelected ? 1 :
                      isRecentlySelected ? 3 : 1,
                    opacity: 1,
                    fillColor: allDistrictsSelected ? "#aaaaaa" : "#ff7800",
                    fillOpacity: allDistrictsSelected ? 0.3 : 0.3,
                    interactive: true,
                    bubblingMouseEvents: false
                  }}
                  eventHandlers={{
                    click: handleDistrictClick,
                    mouseover: handleDistrictMouseOver,
                    mouseout: (e) => handleDistrictMouseOut(e, gisId)
                  }}
                  onEachFeature={(feature, layer) => {
                    const displayName = getRegionDisplayName(gisId, feature, 'District');

                    // Add tooltip
                    layer.bindTooltip(`<b>${displayName}</b>`, {
                      permanent: false,
                      direction: 'top',
                      className: 'map-tooltip'
                    });

                    // Add popup if feature has name property
                    if (feature.properties && feature.properties.name) {
                      layer.bindPopup(`<b>${feature.properties.name}</b>`);
                    }
                  }}
                />
              );
            })}

            {/* Block boundary layers */}
            {Object.entries(blockLayers).map(([gisId, geoJson]) => {
              const isRecentlySelected = selectedBlockIds[selectedBlockIds.length - 1] === gisId;

              return (
                <GeoJSON
                  key={gisId}
                  data={geoJson}
                  style={{
                    color: isRecentlySelected ? "#0000ff" : "#0000FF",
                    weight: isRecentlySelected ? 3 : 1,
                    opacity: 1,
                    fillColor: "#0000FF",
                    fillOpacity: 0.4,
                    maxZoom: 14
                  }}
                  eventHandlers={{
                    click: handleBlockClick,
                    mouseover: handleBlockMouseOver,
                    mouseout: (e) => handleBlockMouseOut(e, gisId)
                  }}
                  onEachFeature={(feature, layer) => {
                    const displayName = getRegionDisplayName(gisId, feature, 'Block');

                    // Add tooltip
                    layer.bindTooltip(`<b>${displayName}</b>`, {
                      permanent: false,
                      direction: 'top',
                      className: 'map-tooltip'
                    });

                    // Add popup if feature has name property
                    if (feature.properties && feature.properties.name) {
                      layer.bindPopup(`<b>${feature.properties.name}</b>`);
                    }
                  }}
                />
              );
            })}
          </MapContainer>
        </div>
      </div>
    </FullScreen>
  );
};

export default RMMap;